import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Mail, 
  MessageCircle, 
  Users, 
  Search, 
  CheckSquare, 
  Square,
  Send,
  Phone,
  UserCheck,
  AlertCircle,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Comunicazioni() {
  const [tipoDestinatario, setTipoDestinatario] = useState('volontari'); // 'volontari' o 'soci'
  const [volontari, setVolontari] = useState([]);
  const [soci, setSoci] = useState([]);
  const [selectedDestinatari, setSelectedDestinatari] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  
  // Filter states
  const [filterStato, setFilterStato] = useState('attivo');
  const [copiedLink, setCopiedLink] = useState(null);

  // Copy to clipboard function
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(id);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(id);
      setTimeout(() => setCopiedLink(null), 2000);
    }
  };

  useEffect(() => {
    fetchVolontari();
    fetchSoci();
  }, []);

  useEffect(() => {
    // Reset selezione quando cambia il tipo destinatario
    setSelectedDestinatari([]);
    setSearchTerm('');
  }, [tipoDestinatario]);

  const fetchVolontari = async () => {
    try {
      const response = await axios.get(`${API}/api/volontari`, { withCredentials: true });
      setVolontari(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching volontari:', error);
      setVolontari([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSoci = async () => {
    try {
      const response = await axios.get(`${API}/api/soci`, { withCredentials: true });
      setSoci(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching soci:', error);
      setSoci([]);
    }
  };

  // Get current list based on tipo destinatario
  const currentList = tipoDestinatario === 'volontari' ? (volontari || []) : (soci || []);
  const idField = tipoDestinatario === 'volontari' ? 'volontario_id' : 'persona_id';

  // Filter destinatari - con protezione per array vuoti/null
  const filteredDestinatari = Array.isArray(currentList) ? currentList.filter(d => {
    if (!d) return false;
    const matchesSearch = 
      d.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.cognome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const stato = tipoDestinatario === 'volontari' ? d.stato : d.stato_socio;
    const matchesStato = filterStato === 'tutti' || stato === filterStato;
    return matchesSearch && matchesStato;
  }) : [];

  // Selection handlers
  const toggleSelectDestinatario = (destinatarioId) => {
    setSelectedDestinatari(prev => 
      prev.includes(destinatarioId)
        ? prev.filter(id => id !== destinatarioId)
        : [...prev, destinatarioId]
    );
  };

  const selectAll = () => {
    const allIds = filteredDestinatari.map(d => d[idField]);
    setSelectedDestinatari(allIds);
  };

  const deselectAll = () => {
    setSelectedDestinatari([]);
  };

  const selectWithEmail = () => {
    const withEmail = filteredDestinatari.filter(d => d.email).map(d => d[idField]);
    setSelectedDestinatari(withEmail);
  };

  const selectWithPhone = () => {
    const withPhone = filteredDestinatari.filter(d => d.telefono || d.cellulare).map(d => d[idField]);
    setSelectedDestinatari(withPhone);
  };

  // Get selected destinatari data
  const getSelectedDestinatariData = () => {
    if (!Array.isArray(currentList)) return [];
    return currentList.filter(d => d && selectedDestinatari.includes(d[idField]));
  };

  // EMAIL - Generate mailto link
  const getMailtoLink = () => {
    const selected = getSelectedDestinatariData();
    const emails = selected.filter(d => d.email).map(d => d.email);
    
    if (emails.length === 0) return null;

    // Build mailto URL
    return `mailto:${emails.join(',')}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  };

  // Open email using window.location (different behavior than <a href>)
  const openEmailClient = () => {
    const link = getMailtoLink();
    if (link) {
      window.location.href = link;
    }
  };

  // Gmail web compose
  const openGmailWeb = () => {
    const selected = getSelectedDestinatariData();
    const emails = selected.filter(v => v.email).map(v => v.email);
    if (emails.length === 0) return;
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${emails.join(',')}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(gmailUrl, '_blank');
  };

  // Outlook web compose
  const openOutlookWeb = () => {
    const selected = getSelectedDestinatariData();
    const emails = selected.filter(d => d && d.email).map(d => d.email);
    if (emails.length === 0) return;
    
    const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${emails.join(',')}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(outlookUrl, '_blank');
  };

  // Copy email addresses to clipboard
  const copyEmailAddresses = async () => {
    const selected = getSelectedDestinatariData();
    const emails = selected.filter(d => d && d.email).map(d => d.email);
    
    if (emails.length === 0) {
      alert('Nessun destinatario selezionato ha un indirizzo email.');
      return;
    }

    await copyToClipboard(emails.join('; '), 'emails');
  };

  // WHATSAPP - Build wa.me link
  const getWhatsAppLink = (telefono) => {
    if (!telefono) return null;

    // Clean phone number (remove spaces, dashes, parentheses, dots)
    let cleanPhone = telefono.replace(/[\s\-\(\)\.\/\+]/g, '');
    
    // Handle Italian numbers
    if (cleanPhone.startsWith('00')) {
      cleanPhone = cleanPhone.substring(2);
    } else if (cleanPhone.startsWith('0')) {
      cleanPhone = '39' + cleanPhone.substring(1);
    } else if (cleanPhone.startsWith('3') && cleanPhone.length === 10) {
      cleanPhone = '39' + cleanPhone;
    } else if (!cleanPhone.startsWith('39') && cleanPhone.length <= 10) {
      cleanPhone = '39' + cleanPhone;
    }

    const message = encodeURIComponent(whatsappMessage || '');
    return `https://wa.me/${cleanPhone}${message ? '?text=' + message : ''}`;
  };

  // Stats - con protezione
  const selectedData = getSelectedDestinatariData() || [];
  const selectedWithEmail = selectedData.filter(d => d && d.email).length;
  const selectedWithPhone = selectedData.filter(d => d && (d.telefono || d.cellulare)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055BB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comunicazioni-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0055BB]/10 rounded-full mb-4">
          <Send className="w-8 h-8 text-[#0055BB]" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Comunicazioni
        </h1>
        <p className="text-slate-500 mt-2">
          Invia comunicazioni a Volontari o Soci tramite Email o WhatsApp
        </p>
      </div>

      {/* Selezione tipo destinatario */}
      <div className="flex justify-center gap-4">
        <Button
          variant={tipoDestinatario === 'volontari' ? 'default' : 'outline'}
          onClick={() => setTipoDestinatario('volontari')}
          className={tipoDestinatario === 'volontari' ? 'bg-[#0055BB]' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Volontari ({(volontari || []).length})
        </Button>
        <Button
          variant={tipoDestinatario === 'soci' ? 'default' : 'outline'}
          onClick={() => setTipoDestinatario('soci')}
          className={tipoDestinatario === 'soci' ? 'bg-[#0055BB]' : ''}
        >
          <UserCheck className="w-4 h-4 mr-2" />
          Soci ({(soci || []).length})
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Come funziona questo modulo:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Email:</strong> Cliccando "Apri la tua casella email" si aprirà il tuo programma di posta (Outlook, Gmail, Thunderbird, Mail, etc.) con i destinatari già inseriti. <em>L'email partirà dalla tua casella personale.</em></li>
                <li><strong>WhatsApp:</strong> Cliccando "Apri" si aprirà WhatsApp (Web o App) con il messaggio pronto da inviare al singolo destinatario.</li>
              </ul>
              <p className="mt-2 text-xs text-blue-600 italic">
                Nota: Le comunicazioni non vengono inviate automaticamente dalla piattaforma, ma tramite i tuoi strumenti (client email e WhatsApp).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Destinatari Selection */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Seleziona Destinatari
              </CardTitle>
              <CardDescription>
                Seleziona {tipoDestinatario === 'volontari' ? 'i volontari' : 'i soci'} a cui inviare la comunicazione
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Filter */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={`Cerca ${tipoDestinatario === 'volontari' ? 'volontario' : 'socio'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-volontari"
                  />
                </div>
                <select
                  value={filterStato}
                  onChange={(e) => setFilterStato(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                >
                  <option value="tutti">Tutti</option>
                  <option value="attivo">Attivi</option>
                  <option value="bozza">Bozza</option>
                  <option value="cessato">Cessati</option>
                </select>
              </div>

              {/* Quick Selection Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Seleziona tutti
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll}>
                  <Square className="w-4 h-4 mr-1" />
                  Deseleziona
                </Button>
                <Button variant="outline" size="sm" onClick={selectWithEmail}>
                  <Mail className="w-4 h-4 mr-1" />
                  Con email
                </Button>
                <Button variant="outline" size="sm" onClick={selectWithPhone}>
                  <Phone className="w-4 h-4 mr-1" />
                  Con telefono
                </Button>
              </div>

              {/* Selection Stats */}
              <div className="flex gap-2 text-sm">
                <Badge variant="secondary">
                  <UserCheck className="w-3 h-3 mr-1" />
                  {selectedDestinatari.length} selezionati
                </Badge>
                <Badge variant="outline" className="text-green-600 border-green-300">
                  <Mail className="w-3 h-3 mr-1" />
                  {selectedWithEmail} con email
                </Badge>
                <Badge variant="outline" className="text-blue-600 border-blue-300">
                  <Phone className="w-3 h-3 mr-1" />
                  {selectedWithPhone} con telefono
                </Badge>
              </div>

              {/* Volontari List */}
              <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                {filteredDestinatari.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    Nessun {tipoDestinatario === 'volontari' ? 'volontario' : 'socio'} trovato
                  </div>
                ) : (
                  filteredDestinatari.map((dest) => (
                    <div
                      key={dest[idField]}
                      className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer ${
                        selectedDestinatari.includes(dest[idField]) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleSelectDestinatario(dest[idField])}
                    >
                      <Checkbox
                        checked={selectedDestinatari.includes(dest[idField])}
                        onCheckedChange={() => toggleSelectDestinatario(dest[idField])}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {dest.nome} {dest.cognome}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          {dest.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {dest.email}
                            </span>
                          )}
                          {(dest.telefono || dest.cellulare) && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {dest.telefono || dest.cellulare}
                            </span>
                          )}
                        </div>
                      </div>
                      {(dest.telefono || dest.cellulare) && getWhatsAppLink(dest.telefono || dest.cellulare) && (
                        <a
                          href={getWhatsAppLink(dest.telefono || dest.cellulare)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md"
                          onClick={(e) => e.stopPropagation()}
                          title="Invia WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Message Composition */}
        <div className="space-y-4">
          {/* Email Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Prepara Email
              </CardTitle>
              <CardDescription>
                Componi il messaggio e aprilo nel tuo programma di posta elettronica
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Oggetto
                </label>
                <Input
                  placeholder="Es: Convocazione assemblea volontari"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  data-testid="email-subject"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Messaggio
                </label>
                <Textarea
                  placeholder="Scrivi qui il contenuto dell'email..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  data-testid="email-body"
                />
              </div>
              {/* Email action buttons */}
              <div className="space-y-3">
                {/* Main email button */}
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={openEmailClient}
                  disabled={!getMailtoLink()}
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Apri la tua casella email ({selectedWithEmail} destinatari)
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                
                {/* Alternative options */}
                {getMailtoLink() && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={openGmailWeb}
                      data-testid="open-gmail-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Apri in Gmail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={openOutlookWeb}
                      data-testid="open-outlook-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Apri in Outlook
                    </Button>
                  </div>
                )}
                
                <p className="text-xs text-slate-500 text-center">
                  L'email verrà inviata dall'account attualmente loggato nel browser (Gmail/Outlook)
                </p>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={copyEmailAddresses}
                  disabled={selectedDestinatari.length === 0 || selectedWithEmail === 0}
                  data-testid="copy-emails-btn"
                >
                  {copiedLink === 'emails' ? (
                    <><Check className="w-4 h-4 mr-2" /> Indirizzi copiati!</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-2" /> Copia indirizzi email</>
                  )}
                </Button>
              </div>
              {selectedDestinatari.length > 0 && selectedWithEmail === 0 && (
                <p className="text-xs text-amber-600 text-center">
                  I volontari selezionati non hanno un indirizzo email
                </p>
              )}
            </CardContent>
          </Card>

          {/* WhatsApp Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Prepara WhatsApp
              </CardTitle>
              <CardDescription>
                Componi il messaggio e invialo tramite il tuo WhatsApp (uno alla volta)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Messaggio WhatsApp
                </label>
                <Textarea
                  placeholder="Scrivi qui il messaggio WhatsApp..."
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                  rows={4}
                  data-testid="whatsapp-message"
                />
              </div>
              
              {/* WhatsApp recipients list */}
              {selectedDestinatari.length > 0 && (
                <div className="border rounded-lg p-3 bg-green-50/50">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Clicca per aprire WhatsApp o copia il link:
                  </p>
                  <div className="space-y-2">
                    {getSelectedDestinatariData()
                      .filter(d => d.telefono || d.cellulare)
                      .map(dest => {
                        const phone = dest.telefono || dest.cellulare;
                        const waLink = getWhatsAppLink(phone);
                        return (
                          <div key={dest[idField]} className="flex items-center gap-2 p-2 bg-white rounded border">
                            <MessageCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="font-medium text-sm flex-shrink-0">{dest.nome} {dest.cognome}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">({phone})</span>
                            <div className="flex-1" />
                            <a
                              href={waLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Apri
                            </a>
                            <button
                              onClick={() => copyToClipboard(waLink, dest[idField])}
                              className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300 flex items-center gap-1"
                              title="Copia link"
                            >
                              {copiedLink === dest[idField] ? (
                                <><Check className="w-3 h-3" /> Copiato!</>
                              ) : (
                                <><Copy className="w-3 h-3" /> Copia</>
                              )}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                  {selectedWithPhone === 0 && selectedDestinatari.length > 0 && (
                    <p className="text-xs text-amber-600">
                      I destinatari selezionati non hanno un numero di telefono
                    </p>
                  )}
                </div>
              )}

              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                <strong>Nota:</strong> Se il pulsante "Apri" non funziona, clicca "Copia" e incolla il link in una nuova scheda del browser.
              </div>
            </CardContent>
          </Card>

          {/* Quick Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template Rapidi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => {
                    setEmailSubject('Convocazione attività');
                    setEmailBody('Gentile volontario,\n\nti informiamo che è prevista un\'attività per la quale chiediamo la tua disponibilità.\n\nPer confermare la partecipazione, rispondi a questa email.\n\nGrazie per la collaborazione.\n\nCordiali saluti');
                    setWhatsappMessage('Ciao! Ti informiamo che è prevista un\'attività. Puoi confermare la tua disponibilità?');
                  }}
                >
                  📅 Convocazione attività
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => {
                    setEmailSubject('Promemoria evento');
                    setEmailBody('Gentile volontario,\n\nti ricordiamo che domani è previsto l\'evento al quale hai confermato la partecipazione.\n\nTi aspettiamo!\n\nCordiali saluti');
                    setWhatsappMessage('Ciao! Ti ricordiamo che domani c\'è l\'evento al quale hai confermato la partecipazione. Ti aspettiamo!');
                  }}
                >
                  ⏰ Promemoria evento
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => {
                    setEmailSubject('Richiesta documenti');
                    setEmailBody('Gentile volontario,\n\nti chiediamo di fornire i documenti mancanti per completare la tua iscrizione.\n\nPuoi consegnarli in sede o inviarli via email.\n\nGrazie per la collaborazione.\n\nCordiali saluti');
                    setWhatsappMessage('Ciao! Ti chiediamo di fornire i documenti mancanti per completare l\'iscrizione. Grazie!');
                  }}
                >
                  📄 Richiesta documenti
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => {
                    setEmailSubject('Comunicazione importante');
                    setEmailBody('Gentile volontario,\n\n\n\nCordiali saluti');
                    setWhatsappMessage('');
                  }}
                >
                  ✉️ Messaggio generico
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
